// DESCRIPTION: Pages settings tab — lets admins pick the WordPress page that
// hosts the customer dashboard, and explains where the auto-provided events
// archive and single-event pages live (no setup needed for those).

import { useState, useEffect, useCallback } from '@wordpress/element';
import { __, sprintf } from '@wordpress/i18n';
import {
	SelectControl,
	Spinner,
	Card,
	CardBody,
	CardHeader,
	Notice,
	Button,
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';

/**
 * Roles handled on this tab. Each one maps a settings key to a shortcode the
 * admin can place into a WordPress page, plus the labels shown in the UI.
 *
 * `role` matches the value accepted by POST /settings/create-page on the
 * backend (see Settings_Controller::create_page_for_role).
 */
const PAGE_ROLES = [
	{
		role: 'dashboard',
		key: 'dashboard_page_id',
		title: __( 'Customer dashboard page', 'nemtly-booking' ),
		description: __(
			'The page that hosts the [nemtly_booking_dashboard] shortcode. Linked from confirmation emails and magic-link logins.',
			'nemtly-booking'
		),
		shortcode: 'nemtly_booking_dashboard',
	},
];

/**
 * Pages Settings Component
 *
 * Lets the admin choose which published WordPress page is wired to each
 * plugin shortcode, or create the missing page in one click. Reads/writes
 * the standard plugin settings keys, so manual selections piggyback on the
 * parent's dirty-state tracking and save bar. The inline "create" action
 * saves on the server immediately and uses syncSavedSettings to keep the
 * client in sync without a dirty-state flicker.
 *
 * @since PBOOKING_SINCE
 *
 * @param {Object}   props
 * @param {Object}   props.settings          Current settings object.
 * @param {Function} props.updateSetting     Update a single working-state key (marks dirty).
 * @param {Function} props.syncSavedSettings Sync server-saved settings into working+baseline state.
 * @param {Function} props.addNotice         Show a toast notice (message, type).
 *
 * @return {JSX.Element} The rendered Pages settings tab.
 */
function PagesSettings( {
	settings,
	updateSetting,
	syncSavedSettings,
	addNotice,
} ) {
	const [ pages, setPages ] = useState( [] );
	const [ isLoading, setIsLoading ] = useState( true );
	const [ loadError, setLoadError ] = useState( null );
	const [ creatingRole, setCreatingRole ] = useState( null );

	const loadPages = useCallback( async () => {
		setLoadError( null );

		try {
			const response = await apiFetch( {
				path: '/wp/v2/pages?per_page=100&status=publish&_fields=id,title,link&orderby=title&order=asc',
			} );

			setPages( Array.isArray( response ) ? response : [] );
		} catch ( error ) {
			setLoadError(
				error?.message || __( 'Failed to load pages', 'nemtly-booking' )
			);
		}
	}, [] );

	useEffect( () => {
		let cancelled = false;

		( async () => {
			setIsLoading( true );
			await loadPages();
			if ( ! cancelled ) {
				setIsLoading( false );
			}
		} )();

		return () => {
			cancelled = true;
		};
	}, [ loadPages ] );

	const pageOptions = [
		{ label: __( '— Select a page —', 'nemtly-booking' ), value: 0 },
		...pages.map( ( page ) => ( {
			label: decodeEntities( page.title?.rendered || `#${ page.id }` ),
			value: page.id,
		} ) ),
	];

	const handleCreate = async ( role ) => {
		setCreatingRole( role.role );

		try {
			const response = await apiFetch( {
				path: '/nemtly-booking/v1/settings/create-page',
				method: 'POST',
				data: { role: role.role },
			} );

			const payload = response?.data || response;
			const page = payload?.page;
			const serverSettings = payload?.settings;

			if ( ! page || ! serverSettings ) {
				throw new Error(
					__( 'Unexpected response from server.', 'nemtly-booking' )
				);
			}

			syncSavedSettings( serverSettings );
			await loadPages();

			addNotice(
				page.created
					? sprintf(
							/* translators: %s: page title */
							__(
								'Created page "%s" and linked it.',
								'nemtly-booking'
							),
							page.title
					  )
					: sprintf(
							/* translators: %s: page title */
							__(
								'Linked to existing page "%s".',
								'nemtly-booking'
							),
							page.title
					  ),
				'success'
			);
		} catch ( error ) {
			addNotice(
				error?.message ||
					__( 'Failed to create page.', 'nemtly-booking' ),
				'error'
			);
		} finally {
			setCreatingRole( null );
		}
	};

	return (
		<div className="nb-space-y-6">
			<Card className="nb-border nb-border-slate-200 nb-shadow-sm">
				<CardHeader className="nb-bg-slate-50">
					<div className="nb-flex nb-items-center nb-gap-3">
						<div className="nb-w-8 nb-h-8 nb-bg-slate-600 nb-rounded-lg nb-flex nb-items-center nb-justify-center">
							<svg
								className="nb-w-4 nb-h-4 nb-text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={ 2 }
									d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="nb-text-lg nb-font-semibold nb-text-slate-900">
								{ __( 'Pages', 'nemtly-booking' ) }
							</h3>
							<p className="nb-text-sm nb-text-slate-600">
								{ __(
									'Choose the page for your customer dashboard. Your events listing and individual event pages are provided automatically.',
									'nemtly-booking'
								) }
							</p>
						</div>
					</div>
				</CardHeader>
				<CardBody className="nb-space-y-6">
					{ loadError && (
						<Notice status="error" isDismissible={ false }>
							{ loadError }
						</Notice>
					) }

					{ isLoading ? (
						<div className="nb-flex nb-items-center nb-gap-2 nb-text-slate-500">
							<Spinner />
							<span>
								{ __( 'Loading pages…', 'nemtly-booking' ) }
							</span>
						</div>
					) : (
						PAGE_ROLES.map( ( role ) => {
							const selectedId =
								parseInt( settings[ role.key ], 10 ) || 0;
							const selectedPage = selectedId
								? pages.find(
										( page ) => page.id === selectedId
								  )
								: null;
							const isStale = selectedId > 0 && ! selectedPage;
							const isEmpty = selectedId === 0;
							const isCreating = creatingRole === role.role;
							const showCreateButton = isEmpty || isStale;

							return (
								<div key={ role.key } className="nb-space-y-2">
									<SelectControl
										label={ role.title }
										value={ isStale ? 0 : selectedId }
										options={ pageOptions }
										onChange={ ( value ) =>
											updateSetting(
												role.key,
												parseInt( value, 10 ) || 0
											)
										}
										help={ role.description }
									/>

									{ selectedPage && (
										<p className="nb-text-xs">
											<a
												href={ selectedPage.link }
												target="_blank"
												rel="noreferrer"
												className="nb-text-slate-600 hover:nb-text-slate-800 nb-underline nb-inline-flex nb-items-center nb-gap-1"
											>
												{ sprintf(
													/* translators: %s: page title */
													__(
														'Open "%s" in a new tab',
														'nemtly-booking'
													),
													decodeEntities(
														selectedPage.title
															?.rendered || ''
													)
												) }
												<svg
													className="nb-w-3 nb-h-3"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={ 2 }
														d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
													/>
												</svg>
											</a>
										</p>
									) }

									{ isEmpty && (
										<p className="nb-text-xs nb-text-amber-700 nb-flex nb-items-center nb-gap-1">
											<svg
												className="nb-w-3.5 nb-h-3.5 nb-flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={ 2 }
													d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
												/>
											</svg>
											{ __(
												"No page selected — visitors can't reach this feature.",
												'nemtly-booking'
											) }
										</p>
									) }

									{ isStale && (
										<p className="nb-text-xs nb-text-amber-700 nb-flex nb-items-center nb-gap-1">
											<svg
												className="nb-w-3.5 nb-h-3.5 nb-flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={ 2 }
													d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
												/>
											</svg>
											{ __(
												'The previously selected page is no longer available (deleted, unpublished, or trashed).',
												'nemtly-booking'
											) }
										</p>
									) }

									{ showCreateButton && (
										<div>
											<Button
												variant="secondary"
												isBusy={ isCreating }
												disabled={ isCreating }
												onClick={ () =>
													handleCreate( role )
												}
											>
												{ isCreating
													? __(
															'Creating…',
															'nemtly-booking'
													  )
													: __(
															'Create a page for me',
															'nemtly-booking'
													  ) }
											</Button>
											<p className="nb-text-xs nb-text-slate-500 nb-mt-1">
												{ sprintf(
													/* translators: %s: shortcode tag */
													__(
														'We will publish a page with the [%s] shortcode already inside. You can edit it afterwards.',
														'nemtly-booking'
													),
													role.shortcode
												) }
											</p>
										</div>
									) }
								</div>
							);
						} )
					) }

					<div className="nb-rounded-md nb-border nb-border-info-500 nb-bg-info-50 nb-p-4 nb-space-y-2">
						<h4 className="nb-text-sm nb-font-semibold nb-text-info-700">
							{ __(
								'Events listing & event pages',
								'nemtly-booking'
							) }
						</h4>
						<p className="nb-text-sm nb-text-neutral-700">
							{ __(
								'You don’t need to create these. Visitors browse all events at the /events/ archive, and every event gets its own page automatically. Add /events/ to your navigation menu under Appearance → Menus.',
								'nemtly-booking'
							) }
						</p>
						<p className="nb-text-xs nb-text-neutral-500">
							{ __(
								'Want a curated or filtered list somewhere else? Drop the [nemtly_events_new] shortcode into any page or post.',
								'nemtly-booking'
							) }
						</p>
					</div>
				</CardBody>
			</Card>
		</div>
	);
}

/**
 * Minimal HTML-entity decoder for page titles returned by wp/v2/pages
 * (which arrive with entities like &amp; in rendered titles).
 *
 * @param {string} input
 * @return {string} The decoded text.
 */
function decodeEntities( input ) {
	if ( typeof input !== 'string' ) {
		return '';
	}

	const textarea = document.createElement( 'textarea' );
	textarea.innerHTML = input;
	return textarea.value;
}

export default PagesSettings;
